// Listen as one quiet row: a dot, a status word, Share and Stop. Adam,
// 2026-10-05, on the Calls card in Seamless: "listen would just minimally
// expand this card, not open a whole nother screen. i'd then share, then click
// prep and join the call", and "there can also be a listen button on the prep
// page that does the same thing". The row owns a whole Listen session
// (hooks/useListen.ts), so it mounts when Listen is tapped and ends the
// session when it unmounts or Stop is tapped. In an embed (the Seamless card's
// frame) it tells the page around it what is happening with postMessage.
// The words heard so far show under the row as they come in (QUEUE row 324):
// with Hide and Show on the prep page, always in the frame.

import { useEffect, useRef, useState, type JSX } from "react";
import type { AccountUser, ListenSaved, ListenTarget } from "@breeze/core";
import { listenWord, savedWord, useListen, type ListenSavedState } from "../hooks/useListen";

/** What the frame reports to the page that holds it. */
export interface ListenEmbedMessage {
  readonly breeze: "listen";
  /** "starting", "live" (their side shared), "unshared", "reconnecting", "ended" or "failed". */
  readonly state: "starting" | "live" | "unshared" | "reconnecting" | "ended" | "failed";
  readonly error?: string;
  /** With "ended" after Stop: how the recording ended up. */
  readonly saved?: ListenSaved;
  /** The height in CSS pixels the frame needs to show the row and the
   * transcript under it. Sent with the last state each time it changes; a
   * frame left at the row's own height shows the row alone. */
  readonly height?: number;
}

/** How long "Recording saved" stays up before the row closes. Adam,
 * 2026-10-08: "clicking stop after listening doesn't have any sort of
 * animation or recording saved confirmation". */
export const SAVED_HOLD_MS = 2200;

function tellParent(message: ListenEmbedMessage): void {
  try {
    if (window.parent !== window) window.parent.postMessage(message, "*");
  } catch {
    // Not framed, or the parent is gone.
  }
}

/** A ring that turns while the recording uploads, then a check that draws
 * itself in once it is saved. Nothing when there was no recording. */
export function SavedMark({ saved, size = 18 }: { readonly saved: ListenSavedState; readonly size?: number }): JSX.Element | null {
  if (saved === "none") return null;
  if (saved === "saving") return <span className="lsv lsv--saving" aria-hidden="true" style={{ width: size, height: size }} />;
  return (
    <svg className="lsv lsv--done" aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" data-partial={saved === "partial" ? "1" : "0"}>
      <circle className="lsv__ring" cx="12" cy="12" r="10.5" />
      <path className="lsv__check" d="M7 12.4l3.3 3.3L17.2 8.8" pathLength={1} />
    </svg>
  );
}

/** The row after Stop: the mark and the word, no buttons. */
export function SavedRow({ saved, testId }: { readonly saved: ListenSavedState; readonly testId: string }): JSX.Element {
  return (
    <div className="linl linl--saved" data-testid={testId} data-saved={saved}>
      <SavedMark saved={saved} />
      <span className="linl__word" role="status">
        {savedWord(saved)}
      </span>
    </div>
  );
}

export function ListenInline({
  target,
  me,
  onStop,
  embed = false,
}: {
  readonly target: ListenTarget;
  readonly me: AccountUser;
  readonly onStop: (saved: ListenSaved | null) => void;
  readonly embed?: boolean;
}): JSX.Element {
  const store = useListen(target, me);
  const [stopping, setStopping] = useState(false);
  // Stop here, or a Stop from another page (the tab lets go on its next beat):
  // either way the row shows the recording being saved.
  const closing = stopping || store.saved !== null;
  const heard = store.theirs.status === "on";
  const ready = store.phase === "live" || store.phase === "reconnecting";
  const state: ListenEmbedMessage["state"] =
    store.phase === "failed" ? "failed" : store.phase === "ended" ? "ended" : store.phase === "reconnecting" ? "reconnecting" : !ready ? "starting" : heard ? "live" : "unshared";

  const last = useRef("");
  const lastState = useRef<ListenEmbedMessage["state"]>("starting");
  useEffect(() => {
    // After Stop the frame says "ended" once, when the confirmation is done,
    // so the page around it does not close the row before it is read.
    if (!embed || closing) return;
    const key = `${state}|${store.error ?? ""}`;
    if (key === last.current) return;
    last.current = key;
    lastState.current = state;
    tellParent({ breeze: "listen", state, ...(store.error ? { error: store.error } : {}) });
  }, [embed, closing, state, store.error]);

  // In the frame: how tall the row and its transcript are, so the page around
  // it can make room for the words.
  const whole = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = whole.current;
    if (!embed || !el || typeof ResizeObserver === "undefined") return;
    let sent = 0;
    const report = (): void => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      if (!height || height === sent) return;
      sent = height;
      tellParent({ breeze: "listen", state: lastState.current, height });
    };
    const watch = new ResizeObserver(report);
    watch.observe(el);
    report();
    return () => watch.disconnect();
  }, [embed, closing]);

  const outcome = store.saved && store.saved !== "saving" ? store.saved : null;
  const onStopRef = useRef(onStop);
  onStopRef.current = onStop;
  useEffect(() => {
    if (!closing || !outcome || store.phase !== "ended") return;
    const timer = setTimeout(() => {
      if (embed) tellParent({ breeze: "listen", state: "ended", saved: outcome });
      onStopRef.current(outcome);
    }, SAVED_HOLD_MS);
    return () => clearTimeout(timer);
  }, [closing, outcome, store.phase, embed]);

  const stop = (): void => {
    if (state === "failed") {
      store.end();
      if (embed) tellParent({ breeze: "listen", state: "ended" });
      onStop(null);
      return;
    }
    setStopping(true);
    store.end();
  };

  if (closing) {
    return (
      <div ref={whole} className="linl-wrap">
        <SavedRow saved={store.saved ?? "saving"} testId="listen-inline-saved" />
      </div>
    );
  }

  const shareLabel = heard ? "Share again" : target.app ? `Share ${target.app} audio` : "Share call audio";
  const word =
    state === "failed"
      ? (store.error ?? "Could not start")
      : state === "reconnecting"
        ? "Reconnecting"
        : state === "starting"
          ? "Starting"
          : listenWord(store);

  return (
    <div ref={whole} className="linl-wrap">
      <div className="linl" data-testid="listen-inline" data-state={state}>
        <span className="lsi__dot" aria-hidden="true" data-quiet={heard && !store.paused ? "0" : "1"} />
        <span className="linl__word" role={state === "failed" ? "alert" : "status"}>
          {word}
        </span>
        {ready ? (
          <button type="button" className="lnotes__btn" onClick={() => store.setPaused(!store.paused)} aria-pressed={store.paused}>
            {store.paused ? "Resume" : "Pause"}
          </button>
        ) : null}
        {ready && !heard ? (
          <button type="button" className="lnotes__btn" onClick={() => void store.shareOtherSide()} disabled={store.theirs.status === "asking"}>
            {store.theirs.status === "off" && store.theirs.message ? "Try again" : shareLabel}
          </button>
        ) : null}
        <button type="button" className="lnotes__stop" onClick={stop}>
          {state === "failed" ? "Close" : "Stop"}
        </button>
      </div>
    </div>
  );
}
